import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../pages/landing-page/components/Footer';
import BusinessInfoStep from './components/BusinessInfoStep';
import ContactAndSocialStep from './components/ContactAndSocialStep';
import VisualIdentityStep from './components/VisualIdentityStep';
import OperationsAndDiscountsStep from './components/OperationsAndDiscountsStep';
import VerificationAndQRStep from './components/VerificationAndQRStep';
import Icon from '../../components/AppIcon';
import { authService } from '../../services/authService';

const BusinessRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Business Information
    legalName: '',
    commercialName: '',
    nit: '',
    category: '',
    subcategory: '',
    description: '',
    address: '',
    city: 'Bogotá',
    department: 'Cundinamarca',
    neighborhood: '',
    password: '',
    confirmPassword: '',
    
    // Contact and Social
    contactEmail: '',
    contactPhone: '',
    whatsappNumber: '',
    websiteUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    twitterUrl: '',
    
    // Visual Identity
    logo: null,
    coverImage: null,
    galleryImages: [],
    
    // Operations and Discounts
    operatingHours: {
      monday: { open: '09:00', close: '18:00', closed: false },
      tuesday: { open: '09:00', close: '18:00', closed: false },
      wednesday: { open: '09:00', close: '18:00', closed: false },
      thursday: { open: '09:00', close: '18:00', closed: false },
      friday: { open: '09:00', close: '18:00', closed: false },
      saturday: { open: '10:00', close: '14:00', closed: false },
      sunday: { open: '', close: '', closed: true }
    },
    discountPercentage: '',
    discountDescription: '',
    discountTerms: '',
    discountValidUntil: '',
    
    // Verification
    emailVerified: false,
    verificationCode: '',
    businessDocument: null,
    taxDocument: null,
    qrCodeGenerated: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const steps = [
    { number: 1, title: 'Información del Negocio', icon: 'Building2' },
    { number: 2, title: 'Contacto y Redes', icon: 'Share2' },
    { number: 3, title: 'Identidad Visual', icon: 'Image' },
    { number: 4, title: 'Horarios y Descuentos', icon: 'Calendar' },
    { number: 5, title: 'Verificación y QR', icon: 'CheckCircle' }
  ];

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Step 1: Create Supabase Auth user with business credentials
      console.log('Creating Supabase Auth user for business...');
      const authData = await authService?.signUp(
        formData?.contactEmail,
        formData?.password,
        {
          fullName: formData?.commercialName,
          phone: formData?.contactPhone,
          userType: 'business',
          legalName: formData?.legalName,
          nit: formData?.nit,
          category: formData?.category
        }
      );

      console.log('✅ Business user created in Supabase Auth:', authData?.user?.id);
      console.log('📧 Email:', formData?.contactEmail);
      console.log('🏢 Business:', formData?.commercialName);

      // Step 2: Navigate to login
      console.log('Business registration completed successfully');
      navigate('/business-login', { 
        state: { 
          message: 'Registro exitoso. Por favor inicia sesión con tus credenciales empresariales.',
          email: formData?.contactEmail 
        } 
      });
    } catch (error) {
      console.error('❌ Error during business registration:', error);
      setSubmitError(error?.message || 'Error al completar el registro. Por favor intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BusinessInfoStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <ContactAndSocialStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 3:
        return (
          <VisualIdentityStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 4:
        return (
          <OperationsAndDiscountsStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 5:
        return (
          <VerificationAndQRStep
            formData={formData}
            updateFormData={updateFormData}
            onSubmit={handleSubmit}
            onPrevious={handlePrevious}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted">
      <Header user={null} />
      <main className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="bg-accent/10 text-accent px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider">
                Registro de Empresa
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-foreground mb-4">
              Lleva Tu Negocio a BIZZNET
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Completa tu registro en 5 pasos y comienza a atraer nuevos clientes con descuentos exclusivos
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-12">
            <div className="flex items-center justify-between relative">
              <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200 -z-10">
                <div
                  className="h-full bg-accent transition-all duration-500"
                  style={{ width: `${((currentStep - 1) / (steps?.length - 1)) * 100}%` }}
                />
              </div>

              {steps?.map((step) => (
                <div key={step?.number} className="flex flex-col items-center flex-1">
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                      currentStep >= step?.number
                        ? 'bg-accent text-white shadow-lg transform scale-110'
                        : 'bg-white text-gray-400 border-2 border-gray-200'
                    }`}
                  >
                    {currentStep > step?.number ? (
                      <Icon name="Check" size={28} />
                    ) : (
                      <Icon name={step?.icon} size={28} />
                    )}
                  </div>
                  <div className="mt-3 text-center">
                    <p
                      className={`text-sm font-bold ${
                        currentStep >= step?.number ? 'text-accent' : 'text-gray-400'
                      }`}
                    >
                      Paso {step?.number}
                    </p>
                    <p
                      className={`text-xs mt-1 hidden sm:block ${
                        currentStep >= step?.number ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {step?.title}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
            {renderStepContent()}
          </div>

          {/* Help Section */}
          <div className="mt-8 text-center bg-orange-50 border-2 border-orange-200 rounded-2xl p-6">
            <div className="flex items-center justify-center gap-3">
              <Icon name="HelpCircle" size={24} className="text-orange-600" />
              <p className="text-base text-orange-900 font-semibold">
                ¿Tienes preguntas? Contacta a nuestro equipo comercial: bizznet.latam@gmail.com o +573152495959
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BusinessRegistration;